import { motion } from 'framer-motion';
import { FiShoppingBag, FiCalendar, FiPackage, FiAlertCircle } from 'react-icons/fi';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import StatsCard from '@components/admin/StatsCard';
import { useAdmin } from '@hooks/useAdmin';
import Loading from '@components/common/Loading';

/**
 * AdminDashboard Page
 * Features:
 * - Cards: total orders, pending bookings, low inventory
 * - Charts: sales trends, booking stats
 * - Alerts for low stock
 * - Animations: card slide-up
 */

const AdminDashboard = () => {
  const { dashboardStats, loading } = useAdmin();

  // Sample data - replace with real data from API
  const salesData = [
    { month: 'Jan', sales: 4000 },
    { month: 'Feb', sales: 3000 },
    { month: 'Mar', sales: 5000 },
    { month: 'Apr', sales: 4500 },
    { month: 'May', sales: 6000 },
    { month: 'Jun', sales: 5500 },
  ];

  const bookingsData = [
    { day: 'Mon', bookings: 12 },
    { day: 'Tue', bookings: 19 },
    { day: 'Wed', bookings: 15 },
    { day: 'Thu', bookings: 22 },
    { day: 'Fri', bookings: 18 },
    { day: 'Sat', bookings: 9 },
    { day: 'Sun', bookings: 7 },
  ];

  if (loading) {
    return <Loading text="Loading dashboard..." />;
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Orders"
          value={dashboardStats?.totalOrders || 142}
          icon={FiShoppingBag}
          trend={{ positive: true, value: '+12% from last month' }}
          color="blue"
        />
        <StatsCard
          title="Pending Bookings"
          value={dashboardStats?.pendingBookings || 23}
          icon={FiCalendar}
          trend={{ positive: false, value: '-5% from last week' }}
          color="yellow"
        />
        <StatsCard
          title="Low Stock Items"
          value={dashboardStats?.lowStockItems || 8}
          icon={FiAlertCircle}
          color="red"
        />
        <StatsCard
          title="Total Products"
          value={dashboardStats?.totalProducts || 347}
          icon={FiPackage}
          trend={{ positive: true, value: '+8 new this month' }}
          color="green"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Sales Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold mb-4">Sales Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sales" stroke="#FBBF24" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Bookings Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold mb-4">Weekly Bookings</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={bookingsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="bookings" fill="#1F2937" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Recent Activity & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Low Stock Alerts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FiAlertCircle className="text-red-500 mr-2" />
            Low Stock Alerts
          </h2>
          <div className="space-y-3">
            {[
              { name: 'Security Camera HD-100', stock: 3, sku: 'CAM-HD-100' },
              { name: 'Smart Lock Pro', stock: 5, sku: 'LOCK-PRO-01' },
              { name: 'Alarm System Basic', stock: 2, sku: 'ALM-BAS-50' },
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{item.name}</p>
                  <p className="text-sm text-gray-600">SKU: {item.sku}</p>
                </div>
                <span className="text-red-600 font-semibold">{item.stock} left</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h2 className="text-xl font-semibold mb-4">Recent Orders</h2>
          <div className="space-y-3">
            {[
              { id: '#1234', customer: 'John Doe', amount: 349.99, status: 'Processing' },
              { id: '#1235', customer: 'Jane Smith', amount: 599.99, status: 'Shipped' },
              { id: '#1236', customer: 'Bob Johnson', amount: 199.99, status: 'Delivered' },
            ].map((order, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{order.id}</p>
                  <p className="text-sm text-gray-600">{order.customer}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">£{order.amount}</p>
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded-full">
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;

